import * as z from "zod";
import { deriveOpenExposure } from "./definitions/derive-open-exposure";
import { getAccount } from "./definitions/get-account";
import { getBond } from "./definitions/get-bond";
import { getBondRequest } from "./definitions/get-bond-request";
import { getCredit } from "./definitions/get-credit";
import { getFileLocation } from "./definitions/get-file-location";
import { getFinancialStatement } from "./definitions/get-financial-statement";
import { getIndemnity } from "./definitions/get-indemnity";
import { getMarketSeries } from "./definitions/get-market-series";
import { getParties } from "./definitions/get-parties";
import { getSfaaExperience } from "./definitions/get-sfaa-experience";
import { getUnderwriting } from "./definitions/get-underwriting";
import { listBondRequests } from "./definitions/list-bond-requests";
import { listBonds } from "./definitions/list-bonds";
import { listFinancialPeriods } from "./definitions/list-financial-periods";
import { listMarketSeries } from "./definitions/list-market-series";
import { listRankedSfaaCodes } from "./definitions/list-ranked-sfaa-codes";
import type { LegacyPythonToolName } from "./legacy-python-tools";
import { TOOL_NAME_PATTERN, type ToolDefinition } from "./tool-definition";

const SCOPING_KEYS = new Set([
	"account_id",
	"accountId",
	"surety_account_id",
	"suretyAccountId",
	"context_id",
	"contextId",
	"context_type",
	"contextType",
]);

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const findScopingKey = (schema: unknown): string | undefined => {
	if (!isObject(schema)) return undefined;
	const properties = schema.properties;
	if (isObject(properties)) {
		const forbidden = Object.keys(properties).find((key) => SCOPING_KEYS.has(key));
		if (forbidden) return forbidden;
	}
	for (const value of Object.values(schema)) {
		const found = Array.isArray(value)
			? value.map(findScopingKey).find((key) => key !== undefined)
			: findScopingKey(value);
		if (found) return found;
	}
	return undefined;
};

const assertStrictObject = (schema: unknown, label: string): void => {
	if (!isObject(schema) || schema.type !== "object" || schema.additionalProperties !== false) {
		throw new Error(`${label} must be a strict object schema`);
	}
};

export const assertCatalogInvariants = (definitions: readonly ToolDefinition[]): void => {
	const names = new Set<string>();
	for (const definition of definitions) {
		if (names.has(definition.name)) throw new Error(`Duplicate assistant tool: ${definition.name}`);
		names.add(definition.name);

		if (!TOOL_NAME_PATTERN.test(definition.name)) {
			throw new Error(`Invalid assistant tool name: ${definition.name}`);
		}
		if (definition.contexts.length === 0) {
			throw new Error(`${definition.name} must declare at least one context`);
		}
		if (definition.implementation === "python" && definition.audience !== "model") {
			throw new Error(`${definition.name}: python tools must have model audience`);
		}

		const inputSchema = z.toJSONSchema(definition.input, { io: "input" });
		assertStrictObject(inputSchema, `${definition.name} input`);
		const scopingKey = findScopingKey(inputSchema);
		if (scopingKey) {
			throw new Error(`${definition.name} input contains reserved scoping key: ${scopingKey}`);
		}

		const outputSchema = z.toJSONSchema(definition.output, { io: "output" });
		assertStrictObject(outputSchema, `${definition.name} output`);
		if (!isObject(outputSchema.properties)) {
			throw new Error(`${definition.name} output must declare envelope properties`);
		}
		const keys = Object.keys(outputSchema.properties).sort();
		if (keys.join(",") !== "asOf,data,evidence,truncated") {
			throw new Error(`${definition.name} output must use the tool envelope`);
		}
	}
};

export const TOOL_DEFINITIONS = [
	getFileLocation,
	listFinancialPeriods,
	getFinancialStatement,
	getAccount,
	getParties,
	getIndemnity,
	getUnderwriting,
	getCredit,
	listBondRequests,
	getBondRequest,
	listBonds,
	getBond,
	deriveOpenExposure,
	listMarketSeries,
	getMarketSeries,
	getSfaaExperience,
	listRankedSfaaCodes,
] as const satisfies readonly ToolDefinition[];

/** The names the catalog actually declares, narrowed to literals so a stale name cannot compile. */
export type ToolName = (typeof TOOL_DEFINITIONS)[number]["name"];

/** Every name a persisted tool call can carry, across both namespaces. */
export type AnyToolName = ToolName | LegacyPythonToolName;
